import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  TrashIcon,
  CheckIcon,
  EyeIcon,
  PencilIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';

const AssessmentEdit = () => {
  const navigate = useNavigate();
  const { id: assessmentId } = useParams();

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tests');
  const [showUnpublishWarning, setShowUnpublishWarning] = useState(false);
  useBodyScrollLock(showUnpublishWarning);

  // Test-related state
  const [availableTests, setAvailableTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [testSearchTerm] = useState('');
  const [testFilters] = useState({});
  const [, setAvailableTestFilters] = useState({});
  const [, setTestLoading] = useState(false);

  // Question-related state
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionSearchTerm] = useState('');
  const [questionFilters] = useState({});
  const [, setAvailableQuestionFilters] = useState({});
  const [, setQuestionLoading] = useState(false);

  // Custom question state
  const [customQuestions, setCustomQuestions] = useState([]);
  const [newCustomQuestion, setNewCustomQuestion] = useState({
    title: '',
    content: '',
    type: 'multiple-choice',
    options: ['', ''],
    correctAnswer: '',
    points: 1,
  });

  useEffect(() => {
    if (assessmentId) {
      fetchAssessment();
      fetchAvailableTests();
      fetchAvailableQuestions();
      fetchTestFilters();
      fetchQuestionFilters();
    }
  }, [assessmentId]);

  const fetchAssessment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/assessments/${assessmentId}`);
      setAssessment(response.data.assessment);

      // Set selected content from assessment
      if (response.data.assessment.content) {
        setSelectedTests(response.data.assessment.content.tests || []);
        setSelectedQuestions(response.data.assessment.content.questions || []);
        setCustomQuestions(response.data.assessment.content.customQuestions || []);
      }
    } catch (error) {
      console.error('Error fetching assessment:', error);
      toast.error('Failed to load assessment');
      navigate('/dashboard/assessments');
    } finally {
      setLoading(false);
    }
  }, [assessmentId, navigate]);

  const fetchTestFilters = useCallback(async () => {
    try {
      const response = await axios.get('/api/tests/library-filters');
      setAvailableTestFilters(response.data);
    } catch (error) {
      console.error('Error fetching test filters:', error);
    }
  }, []);

  const fetchQuestionFilters = useCallback(async () => {
    try {
      const response = await axios.get('/api/questions/library-filters');
      setAvailableQuestionFilters(response.data);
    } catch (error) {
      console.error('Error fetching question filters:', error);
    }
  }, []);

  const fetchAvailableTests = useCallback(async () => {
    try {
      setTestLoading(true);
      const response = await axios.get('/api/tests/library', {
        params: {
          search: testSearchTerm,
          ...testFilters,
        },
      });
      setAvailableTests(response.data.tests);
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to load tests');
    } finally {
      setTestLoading(false);
    }
  }, [testSearchTerm, testFilters]);

  const fetchAvailableQuestions = useCallback(async () => {
    try {
      setQuestionLoading(true);
      const response = await axios.get('/api/questions/library', {
        params: {
          search: questionSearchTerm,
          ...questionFilters,
        },
      });
      setAvailableQuestions(response.data.questions);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setQuestionLoading(false);
    }
  }, [questionSearchTerm, questionFilters]);

  const handleUnpublish = async () => {
    try {
      await axios.put(`/api/assessments/${assessmentId}/unpublish`);
      toast.success('Assessment unpublished successfully');
      setAssessment((prev) => ({ ...prev, status: 'draft' }));
      setShowUnpublishWarning(false);
    } catch (error) {
      console.error('Error unpublishing assessment:', error);
      toast.error('Failed to unpublish assessment');
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Update assessment content
      const updatedAssessment = {
        ...assessment,
        content: {
          tests: selectedTests,
          questions: selectedQuestions,
          customQuestions: customQuestions,
        },
      };

      if (customQuestions.length > 0) {
        const questionsPayload = customQuestions.map((question, index) => ({
          _id: question._id,
          title: question.title || question.content || `Question ${index + 1}`,
          content: question.content || question.title || `Question ${index + 1}`,
          type: question.type || 'short-answer',
          options: Array.isArray(question.options) ? question.options : [],
          correctAnswer: question.correctAnswer || '',
          codingMeta: question.codingMeta || null,
          passageMeta: question.passageMeta || null,
          skills: Array.isArray(question.skills) ? question.skills : [],
          points: Math.max(1, Number(question.points) || 1),
          order: index + 1,
        }));

        await axios.put(`/api/assessments/${assessmentId}/custom-questions`, {
          questions: questionsPayload,
        });
      }

      await axios.put(`/api/assessments/${assessmentId}/content`, updatedAssessment);
      toast.success('Assessment updated successfully');

      // Navigate back to assessments list
      navigate('/dashboard/assessments');
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Failed to save assessment');
    }
  };

  const handlePublish = async () => {
    try {
      await axios.put(`/api/assessments/${assessmentId}/publish`);
      toast.success('Assessment published successfully');
      setAssessment((prev) => ({ ...prev, status: 'published' }));
      navigate('/dashboard/assessments');
    } catch (error) {
      console.error('Error publishing assessment:', error);
      toast.error('Failed to publish assessment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Assessment not found</h1>
            <button
              onClick={() => navigate('/dashboard/assessments')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard/assessments')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {assessment.status === 'published' ? 'Edit Assessment' : 'Build Assessment'}
                </h1>
                <p className="mt-2 text-gray-600">{assessment.title}</p>
              </div>
            </div>

            <div className="flex space-x-3">
              {assessment.status === 'published' && (
                <button
                  onClick={() => setShowUnpublishWarning(true)}
                  className="inline-flex items-center px-4 py-2 border border-yellow-300 rounded-md shadow-sm text-sm font-medium text-yellow-700 bg-white hover:bg-yellow-50"
                >
                  <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                  Unpublish to Edit
                </button>
              )}

              {assessment.status === 'draft' && (
                <button
                  onClick={handlePublish}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Publish Assessment
                </button>
              )}

              <button
                onClick={handleSaveChanges}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </div>

        {/* Status Warning */}
        {assessment.status === 'published' && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Published Assessment</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This assessment is currently published and being used by candidates. To make
                    changes, you'll need to unpublish it first, which will change its status to
                    draft.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assessment Content Builder */}
        <div className="bg-white rounded-lg shadow">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('tests')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'tests'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Tests ({selectedTests.length}/5)
              </button>
              <button
                onClick={() => setActiveTab('questions')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'questions'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Questions ({selectedQuestions.length})
              </button>
              <button
                onClick={() => setActiveTab('custom')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'custom'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Custom Questions ({customQuestions.length})
              </button>
              <button
                onClick={() => setActiveTab('proctoring')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'proctoring'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Proctoring
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'tests' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Select Tests</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Choose up to 5 tests to include in your assessment. Tests will be presented in the
                  order shown below.
                </p>

                {/* Test Selection Interface */}
                <div className="space-y-4">
                  {/* Selected Tests */}
                  {selectedTests.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Selected Tests</h4>
                      <div className="space-y-2">
                        {selectedTests.map((test, index) => (
                          <div
                            key={test.test}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                          >
                            <span className="text-sm font-medium text-gray-900">
                              {index + 1}. {test.testTitle || `Test ${index + 1}`}
                            </span>
                            <button
                              onClick={() => {
                                setSelectedTests((prev) => prev.filter((_, i) => i !== index));
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Available Tests */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Available Tests</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {availableTests.map((test) => (
                        <div
                          key={test._id}
                          className="flex items-center justify-between p-3 border rounded-md"
                        >
                          <div>
                            <h5 className="font-medium text-gray-900">{test.title}</h5>
                            <p className="text-sm text-gray-600">{test.description}</p>
                          </div>
                          <button
                            onClick={() => {
                              if (selectedTests.length < 5) {
                                setSelectedTests((prev) => [
                                  ...prev,
                                  { test: test._id, order: prev.length + 1 },
                                ]);
                              } else {
                                toast.error('Maximum 5 tests allowed');
                              }
                            }}
                            disabled={selectedTests.length >= 5}
                            className="px-3 py-1 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Add
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'questions' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Select Questions</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Choose individual questions to include in your assessment.
                </p>

                {/* Question Selection Interface */}
                <div className="space-y-4">
                  {/* Selected Questions */}
                  {selectedQuestions.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Selected Questions</h4>
                      <div className="space-y-2">
                        {selectedQuestions.map((question, index) => (
                          <div
                            key={question.question}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                          >
                            <span className="text-sm font-medium text-gray-900">
                              {index + 1}. {question.questionTitle || `Question ${index + 1}`}
                            </span>
                            <button
                              onClick={() => {
                                setSelectedQuestions((prev) => prev.filter((_, i) => i !== index));
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Available Questions */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Available Questions</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {availableQuestions.map((question) => (
                        <div
                          key={question._id}
                          className="flex items-center justify-between p-3 border rounded-md"
                        >
                          <div>
                            <h5 className="font-medium text-gray-900">{question.title}</h5>
                            <p className="text-sm text-gray-600">{question.content}</p>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedQuestions((prev) => [
                                ...prev,
                                { question: question._id, order: prev.length + 1 },
                              ]);
                            }}
                            className="px-3 py-1 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
                          >
                            Add
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'custom' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Custom Questions</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Create your own custom questions for this assessment.
                </p>

                {/* Custom Questions Interface */}
                <div className="space-y-4">
                  {/* Existing Custom Questions */}
                  {customQuestions.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Existing Custom Questions</h4>
                      <div className="space-y-2">
                        {customQuestions.map((question, index) => (
                          <div key={index} className="p-3 bg-gray-50 rounded-md">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium text-gray-900">{question.title}</h5>
                              <button
                                onClick={() => {
                                  setCustomQuestions((prev) => prev.filter((_, i) => i !== index));
                                }}
                                className="text-red-600 hover:text-red-800"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{question.content}</p>
                            <div className="text-xs text-gray-500">
                              Type: {question.type} | Points: {question.points}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add New Custom Question */}
                  <div className="border rounded-md p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Add New Custom Question</h4>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Question title"
                        value={newCustomQuestion.title}
                        onChange={(e) =>
                          setNewCustomQuestion((prev) => ({ ...prev, title: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                      <textarea
                        placeholder="Question content"
                        value={newCustomQuestion.content}
                        onChange={(e) =>
                          setNewCustomQuestion((prev) => ({ ...prev, content: e.target.value }))
                        }
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                      <div className="flex space-x-3">
                        <select
                          value={newCustomQuestion.type}
                          onChange={(e) =>
                            setNewCustomQuestion((prev) => ({ ...prev, type: e.target.value }))
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="multiple-choice">Multiple Choice</option>
                          <option value="true-false">True/False</option>
                          <option value="short-answer">Short Answer</option>
                        </select>
                        <input
                          type="number"
                          placeholder="Points"
                          min="0.1"
                          step="any"
                          value={newCustomQuestion.points}
                          onChange={(e) =>
                            setNewCustomQuestion((prev) => ({
                              ...prev,
                              points: parseFloat(e.target.value) || 1,
                            }))
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md w-20"
                        />
                      </div>
                      <button
                        onClick={() => {
                          if (newCustomQuestion.title && newCustomQuestion.content) {
                            setCustomQuestions((prev) => [...prev, { ...newCustomQuestion }]);
                            setNewCustomQuestion({
                              title: '',
                              content: '',
                              type: 'multiple-choice',
                              options: ['', ''],
                              correctAnswer: '',
                              points: 1,
                            });
                          }
                        }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      >
                        Add Question
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'proctoring' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Proctoring Configuration</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Configure proctoring settings to monitor candidate behavior during the assessment.
                </p>

                {/* Proctoring Settings */}
                <div className="space-y-6">
                  {/* Full Screen Monitoring */}
                  <div className="border rounded-md p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Full Screen Monitoring</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Enable full screen monitoring</span>
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Force full screen mode</span>
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-gray-700">
                          Tolerance limit (exits allowed):
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          defaultValue="3"
                          className="px-3 py-2 border border-gray-300 rounded-md w-20"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tab Switching Monitoring */}
                  <div className="border rounded-md p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Tab Switching Monitoring</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">
                          Enable tab switching detection
                        </span>
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Block tab switching</span>
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-gray-700">
                          Tolerance limit (switches allowed):
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          defaultValue="2"
                          className="px-3 py-2 border border-gray-300 rounded-md w-20"
                        />
                      </div>
                    </div>
                  </div>

                  {/* AI Detection */}
                  <div className="border rounded-md p-4">
                    <h4 className="font-medium text-gray-900 mb-3">AI Assistance Detection</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Enable AI detection</span>
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Detect copy/paste activity</span>
                        <input
                          type="checkbox"
                          defaultChecked
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Detect multiple screens</span>
                        <input
                          type="checkbox"
                          defaultChecked
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Data Retention */}
                  <div className="border rounded-md p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Data Retention</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-gray-700">Retain data for (days):</span>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          defaultValue="30"
                          className="px-3 py-2 border border-gray-300 rounded-md w-20"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Auto-delete expired data</span>
                        <input
                          type="checkbox"
                          defaultChecked
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="text-center">
                    <button className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                      Save Proctoring Settings
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Unpublish Warning Modal */}
        {showUnpublishWarning && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <ExclamationTriangleIcon className="mx-auto flex items-center justify-center h-12 w-12 text-yellow-400" />
                <h3 className="text-lg font-medium text-gray-900 mt-2">Unpublish Assessment?</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    This will change the assessment status from published to draft, allowing you to
                    make changes. Candidates will no longer be able to take this assessment until
                    you publish it again.
                  </p>
                </div>
                <div className="items-center px-4 py-3">
                  <button
                    onClick={handleUnpublish}
                    className="px-4 py-2 bg-yellow-600 text-white text-base font-medium rounded-md w-24 shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-300 mr-2"
                  >
                    Unpublish
                  </button>
                  <button
                    onClick={() => setShowUnpublishWarning(false)}
                    className="px-4 py-2 bg-gray-600 text-white text-base font-medium rounded-md w-24 shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentEdit;
